// Mirror of crates/hrth-proto (the source of truth). Binary fields are base64url strings.

export type UserId = string // username@domain
export type Uuid = string // UUIDv7
export type B64 = string

export interface DeviceRef {
  user_id: UserId
  device_id: Uuid
}

export interface ErrorBody {
  code: string
  message: string
  current_epoch?: number
}

export interface Features {
  calls: boolean
  /** Push notifications are on: the server's VAPID public key (base64url). */
  push_key?: string
}

export interface InstanceInfo {
  domain: string
  name: string
  version: string
  features?: Features
  /** Colours for designed pictures and space symbols. */
  palette?: string[]
  /** Fonts the admins chose; the app's own where unset. */
  fonts?: ServerFonts
  /** People may upload profile photos (false: designed pictures only, photos hidden). */
  profile_photos?: boolean
}

export interface ServerFonts {
  heading?: ServerFont
  text?: ServerFont
}

/** A font file on the server, at `/fonts/{role}?v={version}`. */
export interface ServerFont {
  name: string
  /** As CSS `format()` names it: woff2, woff, truetype, opentype. */
  format: string
  version: number
  /** Where it was fetched from, if linked. */
  source?: string
}

export interface CallTokenResponse {
  url: string
  token: string
  room: string
  identity: string
}

export interface UserProfile {
  user_id: UserId
  display_name: string
  /** Avatar version (Unix ms) when the user has a profile picture. */
  avatar?: number
  /** A designed picture (pixel icon text), instead of a photo. */
  pixels?: string
}

export interface RegisterRequest {
  invite_code: string
  username: string
  display_name: string
  device_id: Uuid
  public_key: B64
  signature: B64
}

export interface RegisterResponse {
  user_id: UserId
  device_id: Uuid
  token: string
  space_id: Uuid | null
}

export interface MeResponse {
  profile: UserProfile
  device_id: Uuid
  is_admin: boolean
  key_packages_remaining: number
  /** The server's rules let me create a space now (policy and cap). */
  can_create_spaces: boolean
  /** The server lets members attach their own storage, or I'm an admin (`/me/storage`). */
  can_link_storage?: boolean
  /** Some server storage is open to me for libraries of my own. */
}

/** Who may create spaces: any account, or admins plus accounts they approve. */
export type SpaceCreation = 'everyone' | 'approved'

/** The server's self-update (`GET /admin/update`; `POST /admin/update/check` checks now). */
export interface UpdateStatus {
  /** The running version. */
  version: string
  /** Why this server doesn't update itself (development builds), if it doesn't. */
  disabled?: string | null
  checking: boolean
  /** The version being installed; the server restarts into it. */
  installing?: string | null
  /** Unix ms. */
  last_checked?: number | null
  last_error?: string | null
  /** The newest release at the last check. */
  latest?: string | null
  /** An update that didn't start and was rolled back, until the next good one. */
  rolled_back?: string | null
}

/** Server-wide settings admins change at runtime. */
export interface ServerSettings {
  name: string
  space_creation: SpaceCreation
  /** Most spaces a non-admin may own; 0 = no limit. */
  max_spaces_per_user: number
  /** Colours for designed pictures and space symbols (2–16). */
  palette: string[]
  /** People may upload profile photos; off: designed pictures only. */
  profile_photos: boolean
  /** Devices may get push notifications (through Apple's, Google's, Mozilla's or Microsoft's push service). */
  push_notifications: boolean
  /** Largest attachment, in MB (MiB), at most `attachment_ceiling_mb`. */
  max_attachment_mb: number
  /** The most `max_attachment_mb` may be, set in the server's configuration. Ignored on PUT. */
  attachment_ceiling_mb: number
  /** Largest git repository, in MB; 0 = no limit. */
  max_repository_mb: number
  /** All git repositories together, in MB; 0 = no limit. */
  max_repositories_total_mb: number
  /** Most people in one watch party or listening room. */
  max_party_size: number
  /** Spaces with more members than this can't have libraries shared or start parties; 0 = off. */
  large_space_members: number
  /** Members may attach their own storage and create libraries in it (admins always may). */
  member_storage?: boolean
  /** Most storage locations a non-admin may own; 0 = no limit. */
  max_locations_per_user?: number
  /** Most libraries (repositories included) a non-admin may own; 0 = no limit. */
  max_libraries_per_user?: number
}

/** node: a member's hrth-node; its record (name) sealed for the owner's devices. */
export type StorageKind = 'disk' | 's3' | 'node'

/** A place libraries and attachments keep their (encrypted) files, as listed for admins. */
export interface StorageLocation {
  /** A configured location's id is its name. */
  id: string
  name: string
  kind: StorageKind
  path?: string
  endpoint?: string
  region?: string
  bucket?: string
  prefix?: string
  /** Set in the server's configuration, so it can't be changed or removed in the app. */
  configured: boolean
  /** The member who attached it; absent for the server's own locations. */
  owner?: UserId
  /** A member's bucket the server can't reach: its record sealed for its owner's devices
   *  (kind `storage-location:<id>`); then `name` is its id and the bucket fields are empty. */
  sealed?: string
  /** The server's own locations, as listed for admins: what it's for, and what's used. */
  policy?: LocationPolicy
  used?: LocationUse
  libraries: number
  attachments: number
  /** Something to know about a location just added (e.g. its CORS rule couldn't be set). */
  notice?: string
  /** A node: whether it is connected. */
  online?: boolean
  /** A node: whether it has claimed its pairing link. */
  linked?: boolean
  /** My node: its upload limits for other people, sealed under its key (`node-rules:<id>`). */
  rules?: string
  /** My node: who those limits name (the server knows this much, never the limits). */
  open_to?: NodeOpenTo
}

/** Who a node is open to (HRTH-182). */
export interface NodeOpenTo {
  users: UserId[]
  spaces: string[]
  roles: string[]
}

/** Someone's node open to me: its name and my room there come from the node (`POST /storage/{id}/ask`). */
export interface OpenNode {
  id: string
  owner: UserId
  online: boolean
  /** My libraries there. */
  libraries: number
}

/** What's kept in the server's own data directory, which is always there. */
export interface DataDirStorage {
  path: string
  attachments: number
  /** Space attachments may use here, MiB; null = no limit (PUT /admin/data-dir/policy). */
  attachments_mb?: number | null
  /** Bytes attachments use here, uploads in progress included. */
  attachment_bytes?: number
}

/** How much space attachments may use in a server location (STORAGE.md §2.6), MiB; null = no limit.
 *  Server storage holds attachments only (HRTH-183). */
export interface LocationPolicy {
  attachments_mb: number | null
}

/** Bytes used in a location, uploads in progress included. */
export interface LocationUse {
  attachments: number
}

export interface StorageOverview {
  /** Missing from servers older than the client. */
  data_dir?: DataDirStorage
  locations: StorageLocation[]
  /** Where new attachments go: a location id, or null for the server's data directory. */
  attachments: string | null
  attachments_configured: boolean
  /** Folders admins may add on-disk locations under; empty: only in configuration. */
  disk_roots: string[]
}

/** `GET /me/storage`: my own storage locations and what the server allows. */
export interface MyStorage {
  allowed: boolean
  locations: StorageLocation[]
  /** Libraries I own. */
  libraries: number
  /** Most locations and libraries I may own; 0 = no limit (always 0 for admins). */
  max_locations: number
  max_libraries: number
  /** Other people's nodes open to me. */
  open_nodes?: OpenNode[]
}

/** Body of `POST /admin/storage` and `POST /me/storage` (members: S3 only). */
export interface AddStorageLocation {
  name: string
  kind: StorageKind
  path?: string
  endpoint?: string
  region?: string
  bucket?: string
  prefix?: string
  access_key_id?: string
  secret_access_key?: string
  /** POST /me/storage: a bucket the server can't reach, as its sealed record (kind s3, no other fields). */
  sealed?: string
  /** With `sealed`: the location id (`loc-…`), chosen here so the record is bound to it. */
  id?: string
}

export interface AdminUserUpdate {
  is_admin?: boolean
  can_create_spaces?: boolean
}

export interface UploadKeyPackagesRequest {
  key_packages: B64[]
  last_resort?: B64 | null
}

export interface ClaimKeyPackagesResponse {
  key_packages: { device: DeviceRef; key_package: B64 }[]
}

export interface CreateInviteRequest {
  space_id?: Uuid | null
  max_uses?: number | null
  expires_in_secs?: number | null
}

export interface InviteInfo {
  code: string
  url: string
  space_id: Uuid | null
  max_uses: number | null
  uses: number
  expires_at: number | null
}

export interface InvitePreview {
  instance_name: string
  domain: string
  inviter: UserProfile
  space_name: string | null
  space_member_count: number | null
}

/** Space ranks; what members may do comes from their custom role. */
export type Role = 'owner' | 'member'
export type ConversationKind = 'channel' | 'dm'

export interface SpaceMember {
  profile: UserProfile
  role: Role
  /** The id of the custom role held, if any: a list with at most one entry (see `SpaceInfo.roles`). */
  roles: string[]
  /** Suspended here until then (Unix ms): no messages, calls or parties in the space. */
  suspended_until?: number
}

/** What a custom role may do; the owner can do all of it. */
export type Perm =
  | 'manage_channels'
  | 'rename_space'
  | 'remove_members'
  | 'manage_repositories'
  | 'hide_messages'
  | 'suspend_members'
  | 'lift_suspensions'
  | 'resolve_reports'
  | 'manage_roles'
  | 'invite_members'

/** A custom role in a space: a named, coloured set of permissions. Roles are ordered, highest first. */
export interface SpaceRole {
  id: string
  name: string
  /** `#rrggbb` */
  color: string
  perms: Perm[]
}

export interface SpaceRoleRequest {
  name: string
  color: string
  perms: Perm[]
}

export interface ConversationInfo {
  id: Uuid
  kind: ConversationKind
  space_id: Uuid | null
  name: string | null
  epoch: number
  last_seq: number
  members: UserId[]
  /** Hosting server when it isn't ours (federation). */
  host?: string
  /** DMs: my private self-group (keys between my devices), not a group DM the others left. Absent from older servers. */
  self_group?: boolean
  /** DMs: a group (people can be added; it may be named; `name` holds that, decrypted) rather than a 1:1. */
  group?: boolean
  /** DMs: who started it (in a group, the one who may remove people). */
  created_by?: UserId
  /** Channels: the colour tinting its background (encrypted on the wire; `#rrggbb` once decrypted). */
  color?: string
}

export interface SpaceInfo {
  id: Uuid
  name: string
  my_role: Role
  members: SpaceMember[]
  channels: ConversationInfo[]
  /** The space symbol (pixel icon text), decrypted on this device; absent if none or unreadable. */
  icon?: string
  /** Custom roles, highest first: the order is the hierarchy. */
  roles: SpaceRole[]
  /** What I may do here. */
  my_perms: Perm[]
  /** I may manage roles: the owner, a server admin, or with Manage roles (then only below my own). */
  can_manage_roles: boolean
  /** Hosting server when it isn't ours (federation). */
  host?: string
}

export interface DeviceInfo {
  device_id: Uuid
  public_key: B64
}

export interface UserDevices {
  user_id: UserId
  devices: DeviceInfo[]
}

export interface SyncResponse {
  spaces: SpaceInfo[]
  conversations: ConversationInfo[]
  /** Conversations I muted (no push notifications). */
  muted?: Uuid[]
  profiles: UserProfile[]
  online?: UserId[]
  devices: UserDevices[]
}

/** Body of `PUT /push/subscription` (`PushSubscription.toJSON()`'s endpoint and keys). */
export interface PushSubscriptionRequest {
  endpoint: string
  p256dh: string
  auth: string
}

export interface MyDevice {
  device_id: Uuid
  created_at: number
  current: boolean
}

/** POST /me/nodes: a node location of mine and the one-time token its node claims. */
export interface NodePairing {
  location: StorageLocation
  token: string
  expires_at: number
}

export interface LinkTokenResponse {
  token: string
  url: string
  expires_at: number
}

/** A completed history-transfer part waiting for this device (HRTH-202); `meta` is sealed. */
export interface TransferPart {
  id: string
  from: Uuid
  meta: B64
  size: number
  completed_at: number
}

export interface LinkPreview {
  profile: UserProfile
  instance_name: string
}

export interface MembershipDelta {
  adds: DeviceRef[]
  removes: DeviceRef[]
  external_join: boolean
}

export interface CommitRequest {
  epoch: number
  commit: B64
  welcome?: B64 | null
  group_info: B64
  delta: MembershipDelta
}

export interface CommitResponse {
  seq: number
  epoch: number
}

export interface LogEntry {
  seq: number
  kind: 'commit' | 'app'
  epoch: number
  sender: DeviceRef
  payload: B64
  delta?: MembershipDelta
  created_at: number
}

export interface LogResponse {
  entries: LogEntry[]
  more: boolean
  /** Entries this device hadn't fetched were deleted (30 days): rejoin and carry on. */
  missed?: boolean
}

export interface GroupInfoResponse {
  epoch: number
  group_info: B64
  last_seq: number
}

export interface WelcomeEntry {
  id: number
  conversation_id: Uuid
  welcome: B64
  seq: number
}

// --- Envelope (inside MLS; never seen by the server) ---

export type EnvelopeBody =
  /** `mentions`: people the text names as `@Display Name` (told to the server too, for push). */
  | { type: 'text'; body: string; reply_to?: Uuid; mentions?: UserId[] }
  | { type: 'reaction'; target: Uuid; emoji: string; remove?: boolean }
  | { type: 'edit'; target: Uuid; body: string }
  | { type: 'delete'; target: Uuid }
  /** A moderator hides (hidden: true) or un-hides someone else's message for everyone. */
  | { type: 'hide'; target: Uuid; hidden: boolean }
  | { type: 'typing' }
  | { type: 'media'; media: MediaRef; body?: string }
  | { type: 'library'; item: LibraryRef; body?: string }
  | { type: 'party'; party: Party }
  // Shares a space's name key with my own devices (only in my private self-group).
  | { type: 'space_key'; space: Uuid; key: string }
  | { type: 'library_key'; library: string; key: string }
  | { type: 'storage_key'; location: string; key: string }
  /** Names one of my devices (only in my private self-group); the latest `at` wins. */
  | { type: 'device_name'; device: Uuid; name: string; at: number }
  /** Names a group DM (no name: back to its members' names); `repeat` re-tells people who joined. */
  | { type: 'conv_name'; name?: string; repeat?: boolean }
  | { type: 'unknown' }
  // Local-only: membership notices derived from MLS commits; never sent.
  | { type: 'system'; event: SystemEvent; user: UserId }

/** `missed`: this device was away so long that messages left the server first (HRTH-109). */
export type SystemEvent = 'joined' | 'left' | 'device_added' | 'device_removed' | 'missed'

/** Everything needed to fetch and decrypt an attachment (keys are inside the E2EE message). */
export interface MediaRef {
  server?: string
  id: Uuid
  key: B64
  nonce: B64
  size: number
  chunk: number
  mime: string
  name: string
  thumb?: string
  width?: number
  height?: number
}

/** `frank`: the franking key of a reportable message (revealed when it's reported). */
export type Envelope = { v: number; id: Uuid; ts: number; frank?: string } & EnvelopeBody

// --- WebSocket frames ---

/** How starting a call tells the others (`call/presence`, first join only). */
export type RingMode = 'ring' | 'notify'
/** Why a ring stopped (hrth-proto ws::RingEnd). */
export type RingEnd = 'answered' | 'declined' | 'ended' | 'timeout'
/** How long a call rings (hrth-proto RING_SECS). */
export const RING_SECS = 45

export type ServerFrame =
  | { t: 'ready' }
  | { t: 'pong' }
  | { t: 'log'; conversation_id: Uuid; seq: number }
  | { t: 'welcome' }
  | { t: 'space'; space_id: Uuid }
  | { t: 'conversations' }
  | { t: 'presence'; user_id: UserId; online: boolean }
  | { t: 'call'; conversation_id: Uuid; participants: UserId[] }
  | { t: 'ring'; conversation_id: Uuid; from: UserId }
  | { t: 'ring_ended'; conversation_id: Uuid; reason: RingEnd }
  | { t: 'ring_declined'; conversation_id: Uuid; by: UserId }
  | { t: 'party'; conversation_id: Uuid; participants: UserId[] }
  | { t: 'pull'; library: string; pull: string }
  | { t: 'node'; location: string; online: boolean }
  | { t: 'error'; code: string; message: string }

export type ClientFrame = { t: 'auth'; token: string } | { t: 'ping' } | { t: 'visible'; visible: boolean }

// --- Libraries (end-to-end encrypted, in their owners' buckets and nodes) ---

export interface LibraryInfo {
  /** Stable id for URLs and references (`lib-…`). */
  id: string
  /** Ciphertext under the library key (`library:<id>`). */
  name: string
  items: number
  /** Total bytes of its files. */
  bytes: number
  /** For whoever runs it, and admins: spaces and individual users it's shared with. */
  shared_with: Uuid[]
  shared_users: UserId[]
  /** For whoever runs it, and admins: of `shared_users`, those who may also upload. */
  uploaders: UserId[]
  /** For whoever runs it, and admins: custom space roles whose holders may see it. */
  shared_roles: string[]
  /** Of `shared_with`, spaces whose members may also upload (whoever runs it, and admins). */
  upload_spaces?: Uuid[]
  /** Of `shared_roles`, roles whose holders may also upload (whoever runs it, and admins). */
  upload_roles?: string[]
  /** I run it: its owner (rename, settings, sharing). */
  can_manage: boolean
  /** Who owns it. */
  owner?: UserId
  /** The caller may upload (whoever runs it, and contributors). */
  can_upload: boolean
  kind: LibraryKind
  /** Its storage location: its owner's bucket or node. */
  location?: string
  /** Settings under the library key (`enc:v1:`, kind `library-settings:<id>`). */
  settings?: string
  /** The library key wrapped under the key of each of the caller's spaces it's shared with. */
  key_wraps: LibraryKeyWrap[]
  /** In a sealed location: where its files are and the read key (`library-storage:<id>`), for
   *  everyone who can read it; the write key (`library-storage-write:<id>`), for uploaders. */
  storage?: string
  storage_write?: string
  /** On a node: whether the node is connected, so its files can be played now. */
  node_online?: boolean
  /** On a node, for whoever runs it: its key sealed under the node location's key (`node-library-key:<id>`). */
  node_wrap?: string
}

export type LibraryKind = 'files' | 'git'

export interface LibraryKeyWrap {
  space: Uuid
  wrapped: string
}

export interface CreateLibraryRequest {
  /** Chosen by the client (`lib-…`) so the encrypted name can be bound to it. */
  id: string
  name: string
  kind: LibraryKind
  location: string
  /** Required in a sealed location: the storage records, sealed under the library key. */
  storage?: string
  storage_write?: string
}

/** PUT /libraries/{id}/storage: a library's sealed storage records (no write record: read-only storage). */
export interface LibraryStorageRequest {
  storage: string
  storage_write?: string
}

export type BlobRole = 'original' | 'stream' | 'art' | 'preview'

export interface LibraryBlob {
  id: string
  role: BlobRole
  /** Ciphertext bytes. */
  size: number
}

/** A protected branch (by blinded id): only these roles (and their spaces' owners and admins) may move it. */
export interface RefProtection {
  ref_id: string
  roles: string[]
}

export type PullState = 'open' | 'closed' | 'merged'

/** One event of a pull request; `body` is ciphertext under the library key. */
export interface PullEvent {
  id: string
  author?: UserId
  created_at: number
  body: string
}

/** A pull request; its title, branches and conversation are in its encrypted events. */
export interface Pull {
  id: string
  number: number
  author?: UserId
  state: PullState
  created_at: number
  updated_at: number
  /** The opening event, for lists. */
  opened: PullEvent
  events: number
}

/** Where a pull request stands, for unread badges across repositories (no content). */
export interface PullActivity {
  library: string
  id: string
  number: number
  state: PullState
  updated_at: number
  events: number
  last_author?: UserId
}

export interface PullDetail {
  pull: Pull
  events: PullEvent[]
}

/** A git ref of a repository library: blinded id, encrypted target, version. */
export interface RepoRef {
  id: string
  /** `enc:v1:` under the library key, kind `repo-ref:<library>`; absent once deleted. */
  target?: string
  version: number
}

/** relay: a bucket the server can't reach; the device signs every request and sends it through
 *  POST /library/relay, registering the multipart upload with PUT /library-uploads/{id}/multipart. */
export type BlobUploadMode = 'server' | 's3' | 'relay'

/** A blob upload in progress. */
export interface BlobUpload {
  id: string
  /** The library it's for. */
  library: string
  blob: string
  mode: BlobUploadMode
  size: number
  /** Every part but the last is exactly this long. */
  part_size: number
  /** Server mode: bytes received. */
  received: number
  /** S3 and relay modes: parts uploaded and reported (from 1). */
  parts: number[]
  /** Relay mode: the bucket's multipart upload id, once registered. */
  multipart?: string
}

/** Body of POST /library/blobs/{id}/url in a sealed location: a GET signed on this device. */
export interface BlobUrlRequest {
  signed?: string
}

/** A signed URL for a blob's ciphertext, good until `expires_at`. */
export interface BlobUrl {
  url: string
  expires_at: number
}

/** One file in an encrypted library; `meta` is ciphertext under the library key. */
export interface LibraryEntry {
  id: string
  meta: string
  uploaded_by?: UserId
  created_at: number
  updated_at: number
  blobs: LibraryBlob[]
  can_edit: boolean
}

/** A local account, as listed for admins choosing who may see a library. */
export interface LocalUser {
  user_id: UserId
  display_name: string
  is_admin: boolean
  /** Approved to create spaces. */
  can_create_spaces: boolean
  spaces_owned: number
}

export interface UploadStatus {
  id: string
  size: number
  received: number
}

export interface LibraryRef {
  server?: string
  library: string
  /** An entry of the library (`encrypted`), or, in messages from before HRTH-183, an item of a
   *  folder the server read itself, which can't be played any more. */
  id: string
  kind: string
  title: string
  artist?: string
  duration?: number
  /** Played by reading the entry with the library key, not streamed by the server. */
  encrypted?: boolean
}

/** Something a media element can play: a URL, HLS or not. */
export interface PlayResponse {
  url: string
  hls: boolean
  mime: string
  expires_at: number
}

// --- Watch parties / listening rooms ---

export type PartyMedia = { source: 'library'; item: LibraryRef } | { source: 'attachment'; media: MediaRef }

export interface Party {
  id: string
  action: 'start' | 'state' | 'end'
  queue: PartyMedia[]
  index: number
  position_ms: number
  playing: boolean
  /** Sender's estimate of server time when position_ms was current. */
  at_ms: number
}

// --- Reports (HRTH-94) ---------------------------------------------------------------

export type ReportReason = 'harassment' | 'spam' | 'illegal_content' | 'child_safety' | 'copyright' | 'other'
export type ReportStatus = 'open' | 'in_review' | 'resolved'
export type ModerationKind = 'hide' | 'suspend' | 'remove'

export interface ReportedFile {
  blob: string
  key: string
  nonce: string
  size: number
  chunk: number
  mime: string
}

export interface LibraryReportRequest {
  library: string
  entry: string
  path: string
  title?: string
  artist?: string
  file?: ReportedFile
  reason: ReportReason
  note?: string
}

export interface ReportRequest {
  conversation: Uuid
  seq: number
  sender: UserId
  envelope: Envelope
  reason: ReportReason
  note?: string
}

export interface Report {
  id: string
  /** Absent for direct messages and library files: server admins only. */
  space?: Uuid
  /** A reported message. */
  conversation?: Uuid
  message_id?: string
  /** A reported library file. */
  library?: { library: string; entry: string; path: string }
  /** The one file's key the reporter revealed (admins can open it). */
  file?: ReportedFile
  reporter: UserId
  /** Who sent or uploaded it (unknown for a server folder's files). */
  reported?: UserId
  reason: ReportReason
  note?: string
  content: { kind: string; text: string; attachment?: string; sent_at: number }
  /** The franking check passed: `reported` really sent exactly this. */
  verified: boolean
  status: ReportStatus
  action?: { kind: ModerationKind; by: UserId; at: number; minutes?: number }
  resolved_by?: UserId
  resolved_at?: number
  created_at: number
}
